import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  signal,
} from "@angular/core";
import { CommonModule } from "@angular/common";
import { MatDialogModule, MatDialogRef, MAT_DIALOG_DATA } from "@angular/material/dialog";
import { MatIcon } from "@angular/material/icon";
import { MatTooltip } from "@angular/material/tooltip";
import { Router } from "@angular/router";

import { SharedModule } from "../../../../shared/shared.module";
import { DialogHeaderExportComponent } from "../../../../shared/modules/dialog/dialog-header-export/dialog-header-export.component";
import { ChipsToggleComponent } from "../../../../shared/modules/chips-toggle/chips-toggle.component";
import { RadioCardComponent } from "../../../../shared/components/radio-card/radio-card.component";
import { SnackService } from "../../../../shared/services/snack-service.service";

import { COUVERTURE_COMMANDES_DATA } from "../../models/data.model";
import {
  CouvertureCommandeRow,
  CouvertureStatut,
  deriveCouverture,
  mapRawToRow,
} from "./couverture-commandes.model";
import { exportCouvertureCommandesExcel } from "./couverture-commandes-excel.utils";

export interface CouvertureCommandesDialogData {
  data: COUVERTURE_COMMANDES_DATA;
}

type FilterStatut = "ALL" | CouvertureStatut;

export interface CouvLine {
  row: CouvertureCommandeRow;
  statut: CouvertureStatut;
  reste: number;
  reservableNow: number;
  manque: number;
  couvrablePct: number;
}

@Component({
  selector: "vl-couverture-commandes-dialog",
  standalone: true,
  imports: [
    CommonModule,
    MatDialogModule,
    MatIcon,
    MatTooltip,
    SharedModule,
    DialogHeaderExportComponent,
    ChipsToggleComponent,
    RadioCardComponent,
  ],
  templateUrl: "./couverture-commandes-dialog.component.html",
  styleUrl: "./couverture-commandes-dialog.component.scss",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CouvertureCommandesDialogComponent {
  data = inject<CouvertureCommandesDialogData>(MAT_DIALOG_DATA);
  dialogRef = inject(MatDialogRef<CouvertureCommandesDialogComponent>);
  private snack = inject(SnackService);
  private router = inject(Router);

  exporting = signal(false);
  filter = signal<FilterStatut>("ALL");

  filterOptions = [
    { name: "Tous", value: "ALL" },
    { name: "En manque", value: "MANQUE" },
    { name: "À réserver", value: "A_RESERVER" },
    { name: "Couverts", value: "COUVERT" },
  ];

  horizonLabel = computed(() => {
    const j = this.data.data.horizonJours ?? 14;
    return j === 1 ? "Aujourd'hui" : `${j} jours`;
  });

  /** Toutes les lignes (hors prestations), dérivées. */
  allLines = computed<CouvLine[]>(() => {
    return (this.data.data.produits ?? [])
      .filter((p) => !p.isPrestation)
      .map((p) => {
        const row = mapRawToRow(p);
        const d = deriveCouverture(row);
        return {
          row,
          statut: d.statut,
          reste: d.reste,
          reservableNow: d.reservableNow,
          manque: d.manque,
          couvrablePct: d.couvrablePct,
        };
      });
  });

  /** Stats globales. */
  stats = computed(() => {
    const lines = this.allLines();
    let besoin = 0;
    let couvre = 0;
    let totalReserver = 0;
    let totalManque = 0;
    for (const l of lines) {
      besoin += l.row.besoinUG;
      couvre += Math.min(
        l.row.besoinUG,
        l.row.engageUG + Math.max(0, l.row.stockEligibleUG),
      );
      totalReserver += l.reservableNow;
      totalManque += l.manque;
    }
    return {
      nbProduits: lines.length,
      nbCouvert: lines.filter((l) => l.statut === "COUVERT").length,
      nbAReserver: lines.filter((l) => l.statut === "A_RESERVER").length,
      nbManque: lines.filter((l) => l.statut === "MANQUE").length,
      couvrablePct: besoin > 0 ? Math.round((couvre / besoin) * 100) : 100,
      totalReserver: Math.round(totalReserver * 100) / 100,
      totalManque: Math.round(totalManque * 100) / 100,
    };
  });

  /** Lignes filtrées + triées (manque→à réserver→couvert, puis urgence). */
  lines = computed<CouvLine[]>(() => {
    const f = this.filter();
    const rank: Record<CouvertureStatut, number> = {
      MANQUE: 0,
      A_RESERVER: 1,
      COUVERT: 2,
    };
    return this.allLines()
      .filter((l) => f === "ALL" || l.statut === f)
      .sort((a, b) => {
        if (rank[a.statut] !== rank[b.statut]) return rank[a.statut] - rank[b.statut];
        const da = a.row.premierDeparture ?? "9999-12-31";
        const db = b.row.premierDeparture ?? "9999-12-31";
        return da.localeCompare(db);
      });
  });

  statutLabel(s: CouvertureStatut): string {
    return s === "MANQUE"
      ? "En manque"
      : s === "A_RESERVER"
        ? "À réserver"
        : "Couvert";
  }

  openProduit(line: CouvLine): void {
    this.dialogRef.close();
    void this.router.navigateByUrl(`/preparation/produit/${line.row.articleId}`);
  }

  async exportExcel(): Promise<void> {
    if (this.exporting()) return;
    this.exporting.set(true);
    try {
      const rows = (this.data.data.produits ?? [])
        .filter((p) => !p.isPrestation)
        .map(mapRawToRow);
      await exportCouvertureCommandesExcel(rows, {
        horizonLabel: this.horizonLabel(),
        clientsLabel: "Tous",
        groupesLabel: "Tous",
        generatedAt: new Date(),
      });
      this.snack.success("Export Excel téléchargé");
    } catch (e) {
      console.error("[couverture-commandes] export Excel failed", e);
      this.snack.error("Échec de l'export Excel");
    } finally {
      this.exporting.set(false);
    }
  }
}
